import type { ITenantUsageRow, TActivityLevel, TSortDir, TSortKey } from "./interfaces"

const DAY_MS = 24 * 60 * 60 * 1000
const ACTIVE_DAYS = 30
const IDLE_DAYS = 90

/** Bucket a tenant by how recently anything happened (login or recording). */
export function activityLevel(lastActivityAt: string | null, now: number): TActivityLevel {
  if (!lastActivityAt) return "never"
  const then = new Date(lastActivityAt).getTime()
  if (Number.isNaN(then)) return "never"
  const ageDays = (now - then) / DAY_MS
  if (ageDays <= ACTIVE_DAYS) return "active"
  if (ageDays <= IDLE_DAYS) return "idle"
  return "dormant"
}

/** Epoch ms for a nullable ISO string; null/invalid sort to the bottom. */
function timeValue(value: string | null): number {
  if (!value) return Number.NEGATIVE_INFINITY
  const t = new Date(value).getTime()
  return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t
}

/** Comparator for the chosen column + direction. */
export function compareRows(a: ITenantUsageRow, b: ITenantUsageRow, key: TSortKey, dir: TSortDir): number {
  const factor = dir === "asc" ? 1 : -1
  let diff: number
  switch (key) {
    case "name":
      diff = a.name.localeCompare(b.name)
      break
    case "userCount":
    case "activeUserCount":
    case "meetingCount":
    case "meetingsLast30d":
      diff = a[key] - b[key]
      break
    case "lastLoginAt":
    case "lastMeetingAt":
    case "lastActivityAt":
    case "createdAt":
      diff = timeValue(a[key]) - timeValue(b[key])
      break
  }
  return diff * factor
}

export interface ITenantUsageTotals {
  tenantCount: number
  userCount: number
  activeUserCount: number
  meetingCount: number
  activeTenantCount: number
}

/** How many distinct products the rows actually contain. 0 or 1 hides the product filter. */
export function distinctProducts(rows: readonly { product: string | null }[]): number {
  const products = new Set<string>()
  for (const row of rows) {
    if (row.product) products.add(row.product)
  }
  return products.size
}

/** Aggregate the visible rows into the summary header figures. */
export function summarise(rows: ITenantUsageRow[], now: number): ITenantUsageTotals {
  return rows.reduce<ITenantUsageTotals>(
    (acc, r) => ({
      tenantCount: acc.tenantCount + 1,
      userCount: acc.userCount + r.userCount,
      activeUserCount: acc.activeUserCount + r.activeUserCount,
      meetingCount: acc.meetingCount + r.meetingCount,
      activeTenantCount: acc.activeTenantCount + (activityLevel(r.lastActivityAt, now) === "active" ? 1 : 0),
    }),
    { tenantCount: 0, userCount: 0, activeUserCount: 0, meetingCount: 0, activeTenantCount: 0 },
  )
}
